import { useState, useCallback } from 'react'
import { SnackbarType, SnackbarPosition } from '@/components/ui/Snackbar'

interface SnackbarItem {
    id: string
    message: string
    type: SnackbarType
    position: SnackbarPosition
    duration?: number
    showCloseButton?: boolean
}

interface ShowSnackbarOptions {
    type: SnackbarType
    position?: SnackbarPosition
    duration?: number
    showCloseButton?: boolean
}

export const useSnackbar = () => {
    const [snackbars, setSnackbars] = useState<SnackbarItem[]>([])

    const showSnackbar = useCallback((message: string, options: ShowSnackbarOptions) => {
        const id = Date.now().toString() + Math.random().toString(36).substr(2, 9)

        const newSnackbar: SnackbarItem = {
            id,
            message,
            type: options.type,
            position: options.position || 'bottom-right',
            duration: options.duration ?? 5000,
            showCloseButton: options.showCloseButton ?? true
        }

        setSnackbars(prev => [...prev, newSnackbar])

        // Auto remove after duration + animation time
        const totalTime = (newSnackbar.duration || 5000) + 500
        setTimeout(() => {
            setSnackbars(prev => prev.filter(snackbar => snackbar.id !== id))
        }, totalTime)

        return id
    }, [])

    const hideSnackbar = useCallback((id: string) => {
        setSnackbars(prev => prev.filter(snackbar => snackbar.id !== id))
    }, [])

    const clearAllSnackbars = useCallback(() => {
        setSnackbars([])
    }, [])

    // Convenience methods
    const showSuccess = useCallback((message: string, options?: Omit<ShowSnackbarOptions, 'type'>) => {
        return showSnackbar(message, { ...options, type: 'success' })
    }, [showSnackbar])

    const showError = useCallback((message: string, options?: Omit<ShowSnackbarOptions, 'type'>) => {
        return showSnackbar(message, { ...options, type: 'error' })
    }, [showSnackbar])

    const showWarning = useCallback((message: string, options?: Omit<ShowSnackbarOptions, 'type'>) => {
        return showSnackbar(message, { ...options, type: 'warning' })
    }, [showSnackbar])

    const showInfo = useCallback((message: string, options?: Omit<ShowSnackbarOptions, 'type'>) => {
        return showSnackbar(message, { ...options, type: 'info' })
    }, [showSnackbar])

    return {
        snackbars,
        showSnackbar,
        hideSnackbar,
        clearAllSnackbars,
        showSuccess,
        showError,
        showWarning,
        showInfo
    }
}